<template>
	<div>
		<v-stepper
			v-model:model-value="step"
			non-linear
		>
			<v-stepper-header>
				<v-stepper-item
					:step="`${$gettext('Step')} 1`"
					:title="$gettext('Personal Info')"
					:rules="[() => fieldsHaveErrors('selectedTelephoneNumber', 'selectedAddress')]"
					:value="1"
					editable
				/>
				<v-divider />
				<template
					v-for="(section, index) in visibleSections"
					:key="2 * index"
				>
					<v-stepper-item
						:step="`${$gettext('Step')} ${index + 2}`"
						:value="index + 2"
						:title="`${$gettext(sections[section].title)}`"
						:rules="[() => sectionHasErrors(section)]"
						editable
					/>
					<v-divider />
				</template>
				<v-stepper-item
					:step="`${$gettext('Step')} ${visibleSections.length + 2}`"
					:value="visibleSections.length + 2"
					:title="$gettext('Complete')"
					:rules="[() => fieldsHaveErrors('directDebit', 'truth', 'location')]"
					editable
				/>
			</v-stepper-header>
			<v-stepper-window>
				<v-stepper-window-item
					:value="1"
				>
					<claim-form-personal-info
						v-model:model-value="formData"
						:row="row"
						:user="user"
						:telephone-numbers="telephoneNumbers"
						:addresses="addresses"
						:errors="errors"
					/>
				</v-stepper-window-item>
				<v-stepper-window-item
					v-for="(section, index) in visibleSections"
					:key="index * 2"
					:value="index + 2"
				>
					<claim-form-section
						v-model:question-graph="questionGraph"
						:title="sections[section].title"
						:description="sections[section].description || ''"
						:visible-questions="visibleQuestions.filter(question => questionGraph[question].section === section)"
						:errors="errors"
						@answer="updateFormData"
						@choice="({question, answer}) => handleQuestionSelection(question, answer)"
					/>
				</v-stepper-window-item>
				<v-stepper-window-item
					:value="visibleSections.length + 2"
				>
					<claim-form-complete
						v-model:model-value="formData"
						:errors="errors"
						:deductible="deductible"
						:company-name="companyName"
						:user-i-b-a-n="userIBAN"
					/>
				</v-stepper-window-item>
			</v-stepper-window>
			<template #actions="{ prev, next }">
				<div class="d-flex justify-space-between pl-4 pr-4 pb-4">
					<v-btn
						:disabled="step === 1"
						variant="text"
						@click="prev"
					>
						<translate>
							Previous
						</translate>
					</v-btn>
					<v-btn
						v-if="step === visibleSections.length + 2"
						color="primary"
						variant="flat"
						@click="submitClaim"
					>
						<translate>
							Submit Claim
						</translate>
					</v-btn>
					<v-btn
						v-else
						variant="tonal"
						color="primary"
						@click="next"
					>
						<translate>
							Next
						</translate>
					</v-btn>
				</div>
			</template>
		</v-stepper>
		<claim-form-return-prevention
			v-model:open="returnPrevention"
			:phone-model="row.phoneModel"
		/>
	</div>
</template>
<script>
import ClaimFormPersonalInfo from '../molecules/ClaimFormPersonalInfo.vue';
import ClaimFormReturnPrevention from '../molecules/ClaimFormReturnPrevention.vue';
import ClaimFormComplete from '../molecules/ClaimFormComplete.vue';
import axios from 'axios';
import ClaimFormSection from '../molecules/ClaimFormSection.vue';
import {
	cloneDeep, debounce,
} from 'lodash';
import {
	displayError, inform,
} from 'laravel-front-library';

export default {
	name: 'ClaimForm',
	components: {
		ClaimFormSection,
		ClaimFormPersonalInfo,
		ClaimFormComplete,
		ClaimFormReturnPrevention
	},
	props: {
		row: {
			type: Object,
			required: true,
		},
		deductibles: {
			type: Object,
			required: true,
		},
		user: {
			type: Object,
			required: true,
		},
		telephoneNumbers: {
			type: Array,
			required: true,
		},
		addresses: {
			type: Array,
			required: true,
		},
		sections: {
			type: Object,
			required: true,
		},
		rootQuestion: {
			type: Number,
			required: true,
		},
		postLocation: {
			type: String,
			default: undefined
		},
		failLocation: {
			type: String,
			default: undefined
		},
		successLocation: {
			type: String,
			default: undefined
		},
		companyName: {
			type: String,
			required: true,
		},
		userIBAN: {
			type: String,
			required: true,
		},
	},
	data () {
		return {
			step: 1,
			visibleSections: [],
			visibleQuestions: [this.rootQuestion, ],
			formData: {
				selectedTelephoneNumber: 0,
				alternativeTelephoneNumber: '',
				selectedAddress: 0,
				alternativeAddress: {
					street: '',
					houseNumber: '',
					houseNumberSuffix: '',
					postalCode: '',
					city: '',
					country: 'Netherlands',
				},
				directDebit: false,
				truth: false,
				informationRequest: false,
				location: '',
				answers: {
				},
			},
			errors: {
				selectedTelephoneNumber: [],
				alternativeTelephoneNumber: [],
				selectedAddress: [],
				alternativeAddress: {
					street: [],
					houseNumber: [],
					houseNumberSuffix: [],
					postalCode: [],
					city: [],
					country: [],
				},
				directDebit: [],
				truth: [],
				answers: [],
				answersSpecifics: {
				},
				location: [],
			},
			questionGraph: null,
			returnPrevention: false,
			newClaimRoute: '/dashboard/claims/new'
		};
	},

	computed: {
		/**
		 * null, damage or loss depending on the claim type selected by user
		 *
		 * @returns {string|null}
		 */
		claimType () {
			if (!(this.questionGraph[this.rootQuestion] && this.formData.answers[this.rootQuestion])) {
				return null; // we don't know yet
			}

			if (this.formData.answers[this.rootQuestion] === Object.entries(this.questionGraph[this.rootQuestion].answers)[1][0]) {
				return 'loss';
			}
			return 'damage';

		},

		/**
		 * current relevant deductible
		 *
		 * @returns {number|null}
		 */
		deductible () {
			if (this.claimType) {
				return (this.claimType === 'damage' || !this.deductibles.loss) ? this.deductibles.damage : this.deductibles.loss;
			}
			return null;
		},
	},

	created () {
		this.getQuestionGraph();
		window.addEventListener('beforeunload', this.beforeWindowUnload);
	},

	beforeUnmount () {
		window.removeEventListener('beforeunload', this.beforeWindowUnload);
	},

	methods: {
		/**
		 * If any progress has been made ask if the user wants to leave
		 *
		 * @param {object} e
		 */
		beforeWindowUnload (e) {
			// Cancel the event
			e.preventDefault();
			// Chrome requires returnValue to be set
			e.returnValue = '';
		},

		/**
		 * Returns an object representing form errors which starts resets
		 * to no errors whatsoever so it can be repopulated with new errors.
		 *
		 * @returns {object} - an object containing default values for errors
		 */
		getInitialErrors () {
			return {
				selectedTelephoneNumber: [],
				alternativeTelephoneNumber: [],
				selectedAddress: [],
				alternativeAddress: {
					street: [],
					houseNumber: [],
					houseNumberSuffix: [],
					postalCode: [],
					city: [],
					country: [],
				},
				directDebit: [],
				truth: [],
				answers: [],
				answersSpecifics: {
				},
				location: [],
			};
		},

		/**
		 * Given a list of field names, checks if the error object that
		 * represents form errors has an error for any of those fields
		 *
		 * @param {Array} fields - a variable length list of field names
		 * @returns {boolean} - whether or not any of the listed values have errors
		 */
		fieldsHaveErrors (...fields) {
			for (const field of fields) {
				if (this.errors[field].length) {
					return false;
				}
			}
			return true;
		},

		/**
		 * @param {string} section
		 * @returns {boolean}
		 */
		sectionHasErrors (section) {
			if (!this.fieldsHaveErrors('answers')) {
				for (const key of Object.keys(this.errors.answersSpecifics)) {
					if (this.questionGraph[key].section === section) {
						return false;
					}
				}
			}
			return true;
		},

		/**
		 * Get and set the question graph data
		 *
		 * @returns {void}
		 */
		getQuestionGraph () {
			axios.get('/api/v0/claimformdata/claimforms/questiontree')
				.then(({
					data
				}) => {
					this.questionGraph = data;
					this.visibleSections = [this.questionGraph[this.rootQuestion].section, ];
				});
		},

		/**
		 * @param {string|null} question
		 */
		showQuestion (question) {
			if (question !== null) {
				if (!(this.visibleSections.includes(this.questionGraph[question].section))) {
					this.visibleSections.push(this.questionGraph[question].section);
				}

				if (!(this.visibleQuestions.includes(question))) {
					this.visibleQuestions.push(question);
				}

				if (this.questionGraph[question].type !== 'choice') {
					if (this.questionGraph[question].type === 'date') {
						this.questionGraph[question].answer = {
							'date': null,
							'time': null,
						};
					}
					this.showQuestion(Object.values(this.questionGraph[question].answers)[0].next);
				} else if (this.questionGraph[question].answer !== null) {
					this.showQuestion(this.questionGraph[question].answers[this.questionGraph[question].answer].next);
				}
			}
		},

		/**
		 * @param {string|null} question
		 */
		hideQuestion (question) {
			if (question !== null) {
				if (this.questionGraph[question].type !== 'choice') {
					this.hideQuestion(Object.values(this.questionGraph[question].answers)[0].next);
				} else if (this.questionGraph[question].answer !== null) {
					this.hideQuestion(this.questionGraph[question].answers[this.questionGraph[question].answer].next);
				}
				const section = this.questionGraph[question].section;

				this.visibleQuestions.splice(this.visibleQuestions.indexOf(question), 1);
				if (this.visibleQuestions.filter((q) => this.questionGraph[q].section === section).length === 0) {
					this.visibleSections.splice(this.visibleSections.indexOf(section), 1);
				}
			}
		},

		/**
		 * Updates the formdata with answers
		 *
		 * @returns {void}
		 */
		updateFormData () {
			this.formData.answers = {
			};
			this.visibleQuestions.forEach((question) => {
				if (this.questionGraph[question].answer !== null) {
					if (this.questionGraph[question].answer.time !== undefined) {
						this.formData.answers[question] = `${this.questionGraph[question].answer.date} ${this.questionGraph[question].answer.time}`;
					} else {
						this.formData.answers[question] = this.questionGraph[question].answer;
					}
				}
			});
		},

		/**
		 * Based on what question was selected, show more questions
		 *
		 * @param {number} questionId the id of the question that was answered
		 * @param {number} answerId the id of the answer that was chosen
		 * @returns {void}
		 */
		handleQuestionSelection (questionId, answerId) {
			// If we're changing an old answer we should reset the subsequent questions and answers
			if (this.formData.answers[questionId] !== undefined) {
				this.hideQuestion(this.questionGraph[questionId].answers[this.formData.answers[questionId]].next);
			}
			// Always push the given answer for the given question and add it's child to the visible questions
			this.showQuestion(this.questionGraph[questionId].answers[answerId].next);
			this.updateFormData();

			// This is hardcoded for now, later when we make an interface for editing claim questions this should be made dynamic
			let returnPreventionDialog = true;
			// These are the hardcoded ids that correspond to answers that when combined will give a return prevention popup

			for (const returnPreventionRequirement of ['11', '12', '17', '18', '22', '25', '38', '34', '31', '28', ]) {
				if (!Object.values(this.formData.answers).includes(returnPreventionRequirement)) {
					returnPreventionDialog = false;
				}
			}
			if (returnPreventionDialog === true) {
				// Ask them if they're sure they'd like to continue the claim because their phone seems unbroken
				this.returnPrevention = true;
			}
			// Vue is not detected state change all the time so we need to force it.  Strange.
			this.$forceUpdate();
		},

		/**
		 * Takes the data stored in the form and tries to submit it to the server as a claim.
		 * If successful it redirects to the main claims dashboard, otherwise it displays the errors
		 * in the form.
		 *
		 * @returns {void}
		 */
		submitClaim: debounce(function () {
			// Debounce 500 ms to prevent race conditions in the controller handle method
			this.loading = true;
			const dataToSend = new FormData();
			const currentData = cloneDeep(this.formData);

			for (const [question, answer, ] of Object.entries(currentData.answers)) {
				if (Array.isArray(answer) && answer.length > 0 && answer[0] instanceof File) {
					dataToSend.append(`file_${question}`, answer[0], answer[0].name);
					currentData.answers[question] = 'Uploaded File';
				}
			}
			currentData.damageDateTime = this.damageDateTime;
			dataToSend.set('orderrow_id', this.row.id);
			dataToSend.set('formData', JSON.stringify(currentData));
			this.errors = this.getInitialErrors();
			// Send form
			axios({
				method: 'post',
				url: `${this.postLocation === undefined ? this.newClaimRoute : this.postLocation}?id=${this.row.id}`,
				data: dataToSend,
				config: {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				},
			})
				.then((response) => {
					inform(this.$gettext('Claim Submitted'));
					window.removeEventListener('beforeunload', this.beforeWindowUnload);
					window.location.href = this.successLocation === undefined ? `/dashboard/claims/view?sequence=${response.data.claimSequence}` : this.successLocation;
				}).catch(({
					response: {
						status, data
					}
				}) => {
					switch (status) {
					case 400:
						displayError(this.$gettext('Form Incomplete!'));
						for (const [key, value, ] of Object.entries(data.errors)) {
							this.errors[key] = value;
						}
						break;
					case 403:
						displayError(this.$gettext(data.errors.claim_not_allowed));
						setTimeout(() => {
							this.closeClaimForm();
						}, 2000);
						break;
					default:
						displayError(this.$gettext('An error occurred'));
					}
				}).finally(() => {
					this.loading = false;
				});
		}, 500),
	}
};
</script>
